import {
  Badge,
  Box,
  BreadcrumbList,
  Card,
  Flex,
  Link,
  Separator,
  Typography,
  theme,
} from '@fe-toolkit/web-components/ssr'

import {
  ProgressSteps,
  convertMbpsToGbps,
  formatDateToShortString,
  getBadgeForOrder,
  show,
} from '@giffgaff/broadband-common'

import { customEvent } from '@fe-toolkit/eventing'

import dayjs from 'dayjs'

import { formatPriceFromPenceToPounds } from '@/utils/formatPrice'

import { EUIFlags } from '@/constants'

import { useUIFlags } from '@/context'

import { NumberCircle } from '@/components'

import { CpeOrderCard, OrderDetailsCard, InstallBookingCard, InstallBookingCardData } from './components'

import { calculateProgressState, calculateStepNumber, createProgressStepsList } from './manageOrderUtils'

import { ProfilePlanCard } from '../Profile/components/ProfilePlanCard'

import { EOrderStage } from '../ChangeInstallDates/utils/propTypeHelpers'

import type { CpeOrder, OrderDetail, OrderDetailActiveAppointments } from '@/api/schemas'

export type ManageOrderProps = {
  orderData?: OrderDetail
  cpeOrderData?: CpeOrder[]
}

const ManageOrder = ({ orderData, cpeOrderData }: ManageOrderProps) => {
  const { UIFlags } = useUIFlags()

  const isCpeOrdersFeatureEnabled = UIFlags?.[EUIFlags.CPE_ORDERS_UI_FEATURE_ENABLED]

  const hasUsedReferralCode = !!orderData?.serviceReferral?.redeemedReferralCode

  const appointments = {
    outside: orderData?.appointments?.filter(
      appointment => appointment.visitType === EOrderStage.PRE_WORK && appointment.state === 'ACTIVE',
    ),
    inHome: orderData?.appointments?.filter(
      appointment => appointment.visitType === EOrderStage.IN_HOME && appointment.state === 'ACTIVE',
    ),
  }

  const formatInstallBookingCardData = (appointment: OrderDetailActiveAppointments): InstallBookingCardData => ({
    installationDate: `${formatDateToShortString(appointment.appointmentDate)} ${appointment.formattedArrivalTime}`,
    installMethod:
      appointment.visitType === EOrderStage.PRE_WORK ? orderData?.plan.connectionMethodDescription || '' : undefined,
    contactName:
      appointment.contactFirstName && appointment.contactLastName
        ? `${appointment.contactFirstName} ${appointment.contactLastName}`
        : '-',
    contactPhoneNumber: appointment.contactPhoneNumber ?? '',
    ...(appointment.appointmentNotes
      ? {
          healthAndSafety: appointment.appointmentNotes?.healthAndSafety || '-',
          notesForEngineers: appointment.appointmentNotes?.engineerInstructions || '-',
        }
      : {
          healthAndSafety: appointment.healthAndSafetyNotes || '-',
          notesForEngineers: appointment.notes || '-',
        }),
  })

  const outsideInstallBookingCardData: InstallBookingCardData[] =
    appointments?.outside?.map(appointment => formatInstallBookingCardData(appointment)) || []

  const homeInstallBookingCardData: InstallBookingCardData[] =
    appointments?.inHome?.map(appointment => formatInstallBookingCardData(appointment)) || []

  const badge = getBadgeForOrder(orderData?.stateDescription)

  const isPromoPlan = !!orderData?.plan.promotionalProduct

  const handleChatRequest = () => {
    show()
  }

  return (
    <Box
      key={orderData?.key}
      sx={({ theme }) => ({
        backgroundColor: theme.colors.wildSand,
        maxWidth: theme.layout.maxWidth,
        margin: `0 auto`,
        padding: theme.spacing.small,
        [theme.breakpoints.up('medium')]: {
          paddingTop: theme.spacing.medium,
        },
      })}
    >
      {/* breadcrumbs */}
      <Box
        sx={({ theme }) => ({
          padding: theme.spacing.small,
        })}
      >
        <BreadcrumbList
          items={[
            {
              name: 'Account',
              href: '/profile/broadband',
              onClick: () =>
                customEvent('bb_dashboard_click', { contextType: 'order_page', orderId: orderData?.id ?? '' }),
              testId: 'breadcrumb-account-link',
            },
            { name: 'My order', href: '#' },
          ]}
        />
      </Box>

      {/* Page title */}
      <Typography as="h1" marginBottom={theme.spacing.large} testId="manage-order-page-title" variant="megaphone">
        My Order
      </Typography>

      <Flex
        sx={{
          flexDirection: 'column',
          width: '100%',
          [theme.breakpoints.up('medium')]: {
            gap: theme.spacing.large,
            flexDirection: 'row',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            [theme.breakpoints.up('medium')]: {
              width: '50%',
            },
          }}
        >
          {/* order timeline */}
          <Typography as="h2" marginBottom={theme.spacing.tiny} variant="loudBold">
            Order timeline
          </Typography>

          <Card backgroundColor="white" marginBottom={theme.spacing.medium} padding="small">
            <ProgressSteps
              currentStep={calculateProgressState(orderData, cpeOrderData)}
              steps={createProgressStepsList(appointments, isCpeOrdersFeatureEnabled, cpeOrderData)}
            />
          </Card>

          {/* shows if the member used a referral code during the order process */}
          {hasUsedReferralCode && (
            <Typography
              as="p"
              sx={{
                maxWidth: theme.layout.readableWidth,
                marginBottom: theme.spacing.medium,
              }}
              variant="speak"
            >
              You’ll pocket your brilliant £50 referral reward after you make your third payment. Not long now.{' '}
              <Link href="https://www.giffgaff.com/terms/promotions">{'Terms apply.'}</Link>
            </Typography>
          )}

          {/* outside install bookings */}
          {outsideInstallBookingCardData.length > 0 && (
            <>
              <Typography as="h2" display="flex" marginBottom={theme.spacing.tiny} variant="loudBold">
                <NumberCircle>{calculateStepNumber('outside', orderData, cpeOrderData)}</NumberCircle>
                Outside install
              </Typography>
              {outsideInstallBookingCardData.map(bookingData => (
                <InstallBookingCard
                  key={bookingData.installationDate}
                  data={bookingData}
                  marginBottom={theme.spacing.medium}
                  orderData={orderData}
                  visitType={EOrderStage.PRE_WORK}
                />
              ))}
            </>
          )}

          {/* Router arrives */}
          {isCpeOrdersFeatureEnabled && (
            <>
              {/* Router delivery */}
              {orderData && cpeOrderData && cpeOrderData.length > 0 && (
                <Typography as="h2" marginBottom={theme.spacing.tiny} variant="loudBold">
                  <NumberCircle>{calculateStepNumber('tracking', orderData, cpeOrderData)}</NumberCircle>
                  Router arrives
                </Typography>
              )}
              {orderData &&
                cpeOrderData &&
                cpeOrderData.length > 0 &&
                cpeOrderData.map((cpeOrder, index) => (
                  <CpeOrderCard key={index} cpeOrderData={cpeOrder} orderData={orderData} />
                ))}
            </>
          )}

          {/* home install bookings */}
          {homeInstallBookingCardData.length > 0 && (
            <>
              <Typography as="h2" display="flex" marginBottom={theme.spacing.tiny} variant="loudBold">
                <NumberCircle>{calculateStepNumber('inside', orderData, cpeOrderData)}</NumberCircle>
                Home install
              </Typography>
              {homeInstallBookingCardData.map(bookingData => (
                <InstallBookingCard
                  key={bookingData.installationDate}
                  data={bookingData}
                  marginBottom={theme.spacing.medium}
                  orderData={orderData}
                  visitType={EOrderStage.IN_HOME}
                />
              ))}
            </>
          )}
        </Box>

        <Box
          sx={{
            width: '100%',
            [theme.breakpoints.up('medium')]: {
              width: '50%',
            },
          }}
        >
          {/* order details */}
          <Typography as="h2" marginBottom={theme.spacing.tiny} variant="loudBold">
            Order details
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: theme.spacing.mediumLarge,
            }}
          >
            <ProfilePlanCard
              badgeContent={
                badge ? (
                  <Box as="span" data-testid="plan-card-badge">
                    <Badge backgroundColor={badge.backgroundColor} color={badge.color} variant="murmurBold">
                      {badge.text}
                    </Badge>
                  </Box>
                ) : undefined
              }
              displayName={orderData?.plan.displayName}
              price={
                <>
                  £{orderData?.plan.monthlyPrice}
                  <Typography as="span" variant="speakUp">
                    /mo
                  </Typography>
                </>
              }
              priceNote={
                isPromoPlan ? (
                  <>
                    <Typography as="span" sx={{ display: 'block' }} variant="whisper">
                      Then{' '}
                      <Typography as="span" variant="whisperLoud">
                        {formatPriceFromPenceToPounds(Number(orderData!.plan.postPromotionMonthlyPrice) * 100)}/mo
                      </Typography>{' '}
                    </Typography>
                    from {dayjs(orderData?.plan.promotionEndDate).format('D MMM')}
                  </>
                ) : undefined
              }
              speed={orderData && convertMbpsToGbps(orderData?.plan.downstreamSpeedInMbps)}
            >
              <Separator colour="gallery" marginBottom={theme.spacing.small} marginTop={theme.spacing.small} />
              <OrderDetailsCard handleChatRequest={handleChatRequest} orderData={orderData} />
            </ProfilePlanCard>
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}

export default ManageOrder
